import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminRecurring } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

const LIMIT = 30;

function scoreTrend(
  baseline: number | null,
  current: number | null,
): string {
  if (baseline == null && current == null) return "\u2014";
  if (baseline == null) return String(current);
  if (current == null) return String(baseline);
  return `${baseline} \u2192 ${current}`;
}

export function AdminRecurringPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminRecurring({ page, limit: LIMIT });

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="product-page">
      <h1 className="page-title">Recurring Comparisons</h1>
      {data && (
        <>
          <table className="admin-table text-sm">
            <thead>
              <tr>
                <th>Website</th>
                <th className="text-right">Score</th>
                <th className="text-right">Score &Delta;</th>
                <th className="text-right">New Issues</th>
                <th className="text-right">Templates</th>
                <th className="text-right">Issues Fixed</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {data.recurring.map((run) => (
                <tr key={run.runId}>
                  <td>
                    <Link to={`/admin/websites/${run.websiteId}`}>
                      {run.websiteUrl}
                    </Link>
                  </td>
                  <td className="text-right">
                    {scoreTrend(run.baselineScore, run.currentScore)}
                  </td>
                  <td className="text-right">
                    {run.scoreDelta != null ? (
                      <span
                        className={
                          run.scoreDelta > 0
                            ? "text-success"
                            : run.scoreDelta < 0
                              ? "text-danger"
                              : ""
                        }
                      >
                        {run.scoreDelta > 0 ? "+" : ""}
                        {run.scoreDelta}
                      </span>
                    ) : (
                      <span className="text-muted">&mdash;</span>
                    )}
                  </td>
                  <td className="text-right">
                    {run.issueGroupsNew > 0 ? (
                      <span className="text-danger">+{run.issueGroupsNew}</span>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="text-right">
                    {run.templatesNew > 0 ? `+${run.templatesNew}` : ""}
                    {run.templatesNew > 0 && run.templatesRemoved > 0 ? " / " : ""}
                    {run.templatesRemoved > 0 ? `-${run.templatesRemoved}` : ""}
                    {run.templatesNew === 0 && run.templatesRemoved === 0 ? <span className="text-muted">&mdash;</span> : null}
                  </td>
                  <td className="text-right">{run.issueGroupsResolved}</td>
                  <td>
                    {run.completedAt
                      ? new Date(run.completedAt).toLocaleString("en-IE")
                      : <span className="text-muted">&mdash;</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.recurring.length === 0 && (
            <p className="text-muted text-center py-2">
              No recurring comparisons found.
            </p>
          )}
          <div className="d-flex items-center gap-2 mt-3">
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} &middot; {data.total} total
            </span>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.recurring.length < LIMIT}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
